import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

export type TenantUserProfilePreferences = {
  theme?: string | null;
  dateFormat?: string | null;
  timeFormat?: string | null;
  density?: string | null;
  extra?: Record<string, unknown> | null;
};

export type TenantUserProfile = {
  locale?: string | null;
  userId?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  phone?: string | null;
  timeZone?: string | null;
  profileImageUrl?: string | null;
  profilePictureUrl?: string | null;
  avatarUrl?: string | null;
  photoUrl?: string | null;
  profileVersion?: number | null;
  securityVersion?: number | null;
  mfaEnabled?: boolean | null;
  authenticatorConfigured?: boolean | null;
  recoveryCodesRemaining?: number | null;
  preferences?: TenantUserProfilePreferences | null;
};

export type TenantProfileUpdateRequest = {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  phone?: string | null;
  timeZone?: string | null;
  profileVersion?: number | null;
  securityVersion?: number | null;
  mfaEnabled?: boolean | null;
  mfaCode?: string | null;
  mfaChannel?: TenantMfaChannel | null;
  preferences?: TenantUserProfilePreferences | null;
};

export type TenantChangePasswordRequest = {
  oldPassword: string;
  password: string;
  confirmPassword: string;
  mfaCode?: string | null;
  mfaChannel?: TenantMfaChannel | null;
};

export type TenantResponseDto = {
  message?: string;
  statusCode?: number;
};

export type TenantMfaChannel = 'AUTHENTICATOR_APP' | 'EMAIL' | 'SMS' | 'RECOVERY_CODE';

export type TenantRecoveryCodesResponse = {
  codes?: string[] | null;
  remaining?: number | null;
  generatedAt?: string | null;
};

export type TenantMfaSetupResponse = {
  qrCodeImage?: string | null;
  qrCodeImageUrl?: string | null;
  qrCode?: string | null;
  qrCodeImageBase64?: string | null;
  secret?: string | null;
  manualEntryKey?: string | null;
  otpAuthUrl?: string | null;
};

export type TenantLoginSession = {
  id: string;
  deviceId?: string | null;
  deviceLabel?: string | null;
  userAgent?: string | null;
  browserName?: string | null;
  browserVersion?: string | null;
  osName?: string | null;
  ip?: string | null;
  city?: string | null;
  country?: string | null;
  createdAt?: string | null;
  lastSeenAt?: string | null;
  active?: boolean | null;
  current?: boolean | null;
  revoked?: boolean | null;
};

export type TenantSecurityAuditEvent = {
  id?: number | string | null;
  serviceName?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  entityLabel?: string | null;
  action?: string | null;
  actorId?: string | null;
  actorEmail?: string | null;
  createdAt?: string | null;
  detailsJson?: unknown;
};

export type TenantAccountDeletionRequestStatus =
  | 'PENDING'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'COMPLETED'
  | 'CANCELED';

export type TenantAccountDeletionRequest = {
  id: string;
  userId?: string | null;
  emailSnapshot?: string | null;
  displayNameSnapshot?: string | null;
  accountType?: string | null;
  requesterType?: string | null;
  sourceApp?: string | null;
  status: TenantAccountDeletionRequestStatus;
  reason?: string | null;
  retentionNotes?: string | null;
  adminNotes?: string | null;
  decisionReason?: string | null;
  requestedAt?: string | null;
  reviewedAt?: string | null;
  reviewedByUserId?: string | null;
  reviewedByEmail?: string | null;
  completedAt?: string | null;
  completedByUserId?: string | null;
  completedByEmail?: string | null;
  canceledAt?: string | null;
  createdDate?: string | null;
  lastModifiedDate?: string | null;
  version?: number | null;
};

@Injectable({ providedIn: 'root' })
export class TenantProfileService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiGatewayUrl;
  private readonly authBase = this.authApiBase();
  private readonly profileSubject = new BehaviorSubject<TenantUserProfile | null>(null);
  private readonly profileLoadedSubject = new BehaviorSubject(false);
  private ensurePromise: Promise<TenantUserProfile | null> | null = null;

  readonly profile$ = this.profileSubject.asObservable();
  readonly profileLoaded$ = this.profileLoadedSubject.asObservable();

  getProfile(): TenantUserProfile | null {
    return this.profileSubject.value;
  }

  async ensureProfile(force = false): Promise<TenantUserProfile | null> {
    const cached = this.getProfile();
    if (!force && cached) {
      return cached;
    }

    if (!this.ensurePromise) {
      this.ensurePromise = firstValueFrom(this.refreshProfile()).finally(() => {
        this.ensurePromise = null;
      });
    }

    return this.ensurePromise;
  }

  refreshProfile() {
    return this.http.get<TenantUserProfile>(`${this.base}/api/v1/profile`, { withCredentials: true }).pipe(
      tap((profile) => {
        this.profileSubject.next(profile ?? null);
        this.profileLoadedSubject.next(true);
      }),
      catchError((error) => {
        console.warn('[TenantProfileService] profile lookup failed:', error);
        this.profileSubject.next(null);
        this.profileLoadedSubject.next(true);
        return of(null);
      }),
    );
  }

  updateProfile(request: TenantProfileUpdateRequest) {
    return this.http.patch<TenantUserProfile>(`${this.base}/api/v1/profile`, request, { withCredentials: true }).pipe(
      tap((profile) => {
        this.profileSubject.next(profile ?? null);
        this.profileLoadedSubject.next(true);
      }),
    );
  }

  changePassword(request: TenantChangePasswordRequest) {
    return this.http.put<TenantResponseDto>(`${this.base}/api/v1/profile/change-password`, request, {
      withCredentials: true,
    });
  }

  listLoginSessions() {
    return this.http.get<TenantLoginSession[]>(`${this.base}/api/v1/sessions`, { withCredentials: true });
  }

  listSecurityActivity(page = 0) {
    return this.http.get<{ content: TenantSecurityAuditEvent[]; number: number; last: boolean }>(`${this.authBase}/api/v1/audit/me/page?page=${page}&size=25&securityOnly=true`, { withCredentials: true });
  }

  getActiveAccountDeletionRequest() {
    return this.http.get<TenantAccountDeletionRequest | null>(`${this.authBase}/api/v1/account-deletion-requests/me/active`, {
      withCredentials: true,
    });
  }

  requestAccountDeletion() {
    return this.http.post<TenantAccountDeletionRequest>(
      `${this.authBase}/api/v1/account-deletion-requests/me`,
      {
        requesterType: 'TENANT',
        sourceApp: 'TENANT',
      },
      { withCredentials: true },
    );
  }

  cancelAccountDeletionRequest(requestId: string) {
    return this.http.post<TenantAccountDeletionRequest>(
      `${this.authBase}/api/v1/account-deletion-requests/me/${encodeURIComponent(requestId)}/cancel`,
      {},
      { withCredentials: true },
    );
  }

  revokeLoginSession(sessionId: string) {
    return this.http.post<void>(`${this.base}/api/v1/sessions/${sessionId}/revoke`, {}, { withCredentials: true });
  }

  revokeOtherLoginSessions() {
    return this.http.post<void>(`${this.base}/api/v1/sessions/revoke-others`, {}, { withCredentials: true });
  }

  private authApiBase(): string {
    const trimmed = this.base.replace(/\/+$/, '');
    return trimmed.endsWith('/dev') ? `${trimmed}/auth` : trimmed;
  }

  sendSensitiveActionCode(request: {
    userId: string;
    email?: string | null;
    channel: TenantMfaChannel;
    purpose: 'SENSITIVE_ACTIONS';
  }) {
    return this.http.post<TenantResponseDto>(`${this.base}/api/v1/user/send-token`, request, {
      withCredentials: true,
    });
  }

  setupAuthenticatorMfa(mfaCode?: string | null, mfaChannel?: TenantMfaChannel | null) {
    return this.http.post<TenantMfaSetupResponse>(
      `${this.base}/api/v1/user/mfa/authenticator/setup-authenticated`,
      {
        mfaCode: mfaCode || null,
        mfaChannel: mfaChannel || null,
      },
      { withCredentials: true },
    );
  }

  enableAuthenticatorMfa(code: string) {
    return this.http
      .post<TenantResponseDto>(
        `${this.base}/api/v1/user/mfa/authenticator/enable-authenticated`,
        { code },
        { withCredentials: true },
      )
      .pipe(tap(() => void this.ensureProfile(true)));
  }

  regenerateRecoveryCodes(mfaCode?: string | null, mfaChannel?: TenantMfaChannel | null) {
    return this.http.post<TenantRecoveryCodesResponse>(
      `${this.base}/api/v1/profile/recovery-codes`,
      {
        mfaCode: mfaCode || null,
        mfaChannel: mfaChannel || null,
      },
      { withCredentials: true },
    );
  }

  async updateMfaEnabled(enabled: boolean, mfaCode?: string | null, mfaChannel?: TenantMfaChannel | null) {
    const current = await this.ensureProfile(true);
    return firstValueFrom(this.updateProfile({
      mfaEnabled: enabled,
      mfaCode: mfaCode || null,
      mfaChannel: mfaChannel || null,
      securityVersion: current?.securityVersion ?? null,
    }));
  }

  uploadProfileImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<TenantUserProfile>(`${this.base}/api/v1/profile/image`, formData, {
        observe: 'events',
        reportProgress: true,
        withCredentials: true,
      })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.Response) {
            this.profileSubject.next(event.body ?? null);
            this.profileLoadedSubject.next(true);
          }
        }),
      );
  }

  removeProfileImage() {
    return this.http.delete<TenantUserProfile>(`${this.base}/api/v1/profile/image`, { withCredentials: true }).pipe(
      tap((profile) => {
        this.profileSubject.next(profile ?? null);
      }),
    );
  }

  clearProfile(): void {
    this.profileSubject.next(null);
    this.profileLoadedSubject.next(false);
  }
}
